import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import "./CallsDetailsPage.scss";
import { useCallDetailViewModel } from "../viewmodel/useCallDetailViewModel";
import Loader from "@/core/components/loader/Loader";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { imageList } from "@/core/utils/imageList";
import { setModalIsOpened } from "@/core/store/app";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import { useToast } from "@/core/hooks/useToast";
import { useForm } from "react-hook-form";
import { useUnit } from "effector-react";
import {
  $expertiseTerms,
  $lookingForTerms,
  $sectorsTerms,
  fetchTermsRequested,
} from "@/core/store/terms";
import {
  createIdeaFx,
  updateCallBookmarkFx,
  updateCallInterestFx,
  createCommentFx,
} from "../store";
import { callsService } from "../service/api";
import { $userBasicInfo } from "@/features/user/store";
import { Tags } from "@/core/components/tags/Tags";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { Share } from "@/core/components/share/Share";
import { SEO } from "@/core/components/SEO/SEO";
import { RelatedIdea } from "../model/types";

type IdeaFormData = {
  title: string;
  lookingFor: string[] | string;
  expertise: string[] | string;
  description: string;
};

export const CallDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [userBasicInfo] = useUnit([$userBasicInfo]);
  const { pro_user, is_new, partner, plan } = userBasicInfo || {};
  const [showProModal, setShowProModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  const {
    call,
    loading,
    error,
    updateBookmark,
    updateInterest,
    createIdea,
    createIdeaLoading,
    comments,
    commentsLoading,
    commentsError,
    createComment,
    createCommentLoading,
    createCommentError,
    callGoals,
    callGoalsLoading: _callGoalsLoading,
    callGoalsError: _callGoalsError,
    fetchCallById,
  } = useCallDetailViewModel(id);

  const [expertiseTerms, lookingForTerms, sectorsTerms, fetchTerms] = useUnit([
    $expertiseTerms,
    $lookingForTerms,
    $sectorsTerms,
    fetchTermsRequested,
  ]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IdeaFormData>({
    defaultValues: {
      title: "",
      lookingFor: [],
      expertise: [],
      description: "",
    },
  });

  const onSubmitIdea = (data: IdeaFormData) => {
    if (!call?.id) {
      return;
    }

    // Ensure arrays are properly formatted
    const _lookingFor = Array.isArray(data.lookingFor)
      ? data.lookingFor
      : data.lookingFor
        ? [data.lookingFor]
        : [];
    const expertise = Array.isArray(data.expertise)
      ? data.expertise
      : data.expertise
        ? [data.expertise]
        : [];

    const payload = {
      title: data.title,
      field_description: data.description,
      // field_looking_for: lookingFor,
      field_looking_for: data.lookingFor as [string],
      field_eu_expertise: expertise,
      field_assigned_call: [call.id],
      field_status: isDraft ? "draft" : "pending",
    };

    createIdea(payload);

    // Automatically declare interest for the call when submitting an idea
    if (!call.is_call_declared_interest) {
      // console.log("Auto-declaring interest for call:", call.id);
      updateInterest({ callId: call.id, isBookmarked: true });
    }
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const [ideasToShowExpanded, setIdeasToShowExpanded] = useState(false);
  const [ideasToShow, setIdeasToShow] = useState<RelatedIdea[]>([]);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const [currentPartnerIndex, setCurrentPartnerIndex] = useState(0);
  const mainRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const partnersRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const metadataRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [showAllSectors, setShowAllSectors] = useState(false);

  // Set ideas to show when call data loads
  useEffect(() => {
    if (call) {
      handleSetIdeasToShow(ideasToShowExpanded);
    }
  }, [call]);

  // handles showing either all related ideas or just a subset based on the show parameter
  const handleSetIdeasToShow = (show: boolean) => {
    let ideasList = call?.related_ideas ? call.related_ideas : [];

    if (show && ideasList.length > 0) {
      setIdeasToShow(ideasList);
    } else {
      // set ideasToShow based on the number of related ideas
      if (ideasList.length === 1) {
        setIdeasToShow(ideasList);
      } else if (ideasList.length > 1) {
        setIdeasToShow(ideasList.slice(0, 2));
      } else {
        setIdeasToShow([]);
      }
    }
  };

  // Use API data if available, otherwise show empty array
  const partnersToShow = Array.isArray(call?.interested_partners)
    ? call.interested_partners
    : [];

  const partnersPerSlide = 3;
  const totalSlides = Math.ceil(partnersToShow.length / partnersPerSlide);

  // Export handler
  const handleExportCall = async () => {
    if (!id) {
      toast.showError("Call ID is required for export");
      return;
    }

    setIsExporting(true);

    try {
      const exportBlob = await callsService.exportCall(id);

      // Create a download link
      const url = window.URL.createObjectURL(exportBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `call-${call?.title || id}-export.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.showSuccess("Call exported successfully");
    } catch (error) {
      toast.showError("Failed to export call");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePreviousPartner = () => {
    setCurrentPartnerIndex((prev) => (prev > 0 ? prev - 1 : totalSlides - 1));
  };

  const handleNextPartner = () => {
    setCurrentPartnerIndex((prev) => (prev < totalSlides - 1 ? prev + 1 : 0));
  };

  // Match heights after API data loads
  useEffect(() => {
    if (!loading && call && mainRef.current && sidebarRef.current) {
      // Use ResizeObserver for more reliable height detection
      const observer = new ResizeObserver(() => {
        const main = mainRef.current;
        const sidebar = sidebarRef.current;

        if (
          main &&
          sidebar &&
          contentRef.current &&
          titleRef.current &&
          metadataRef.current &&
          tagsRef.current
        ) {
          // Reset to natural height first
          main.style.height = "auto";
          contentRef.current.style.maxHeight = "none";

          // Get natural heights
          const mainHeight = main.offsetHeight;
          const sidebarHeight = sidebar.offsetHeight;

          // Set both to same height (taller one)
          const targetHeight = Math.max(mainHeight, sidebarHeight);

          if (mainHeight > sidebarHeight) {
            setShowExpandButton(true);
            if (!isExpanded) {
              // Calculate heights of fixed elements
              const titleHeight = titleRef.current.offsetHeight;
              const metadataHeight = metadataRef.current.offsetHeight;
              const tagsHeight = tagsRef.current.offsetHeight;

              // Calculate available height for description
              const availableHeight =
                sidebarHeight - titleHeight - metadataHeight - tagsHeight;

              // Set main container height to match sidebar
              main.style.height = `${sidebarHeight}px`;
              main.style.overflow = "hidden";

              // Set description content max height
              contentRef.current.style.maxHeight = `${Math.max(0, availableHeight - 240)}px`;
              contentRef.current.style.overflow = "hidden";
            } else {
              main.style.height = "auto";
              main.style.overflow = "visible";
              contentRef.current.style.maxHeight = "none";
              contentRef.current.style.overflow = "visible";
            }
          } else {
            setShowExpandButton(false);
            main.style.height = `${targetHeight}px`;
            sidebar.style.height = `${targetHeight}px`;
            contentRef.current.style.maxHeight = "none";
            contentRef.current.style.overflow = "visible";
          }
        }
      });

      observer.observe(mainRef.current);
      observer.observe(sidebarRef.current);

      return () => observer.disconnect();
    }
  }, [loading, call, isExpanded]);

  // Fetch terms for form dropdowns - only run once on mount
  useEffect(() => {
    fetchTerms({ vocId: "expertise" });
    fetchTerms({ vocId: "looking_for" });
    fetchTerms({ vocId: "sectors" });
  }, []); // Empty dependency array - only runs once on mount

  // Handle successful idea creation
  useEffect(() => {
    const unsubscribe = createIdeaFx.doneData.watch(() => {
      setShowIdeaModal(false);
      reset();
      toast.showSuccess("Idea created successfully!");
    });

    return unsubscribe;
  }, [reset, toast]);

  // Handle successful bookmark update
  useEffect(() => {
    const unsubscribe = updateCallBookmarkFx.doneData.watch((response) => {
      const action = response.is_call_bookmarked
        ? "saved"
        : "removed from saved";
      toast.showSuccess(`Call ${action} successfully!`);
    });

    return unsubscribe;
  }, [toast]);

  // Handle successful interest update
  useEffect(() => {
    // console.log("Setting up interest effect watchers");
    const unsubscribeSuccess = updateCallInterestFx.doneData.watch(
      (response) => {
        // console.log(
          // "Component: updateCallInterestFx.doneData triggered:",
          // response,
        // );

        // If response is undefined or doesn't have the expected structure,
        // fall back to checking the current call state
        if (
          response &&
          typeof response === "object" &&
          "is_call_declared_interest" in response
        ) {
          const action = response.is_call_declared_interest
            ? "declared"
            : "undeclared";
          // console.log(
            // "Showing toast (from response):",
            // `Interest ${action} successfully!`,
          // );
          toast.showSuccess(`Interest ${action} successfully!`);
        } else if (call) {
          // Fallback: use current call state (which was optimistically updated)
          const action = call.is_call_declared_interest
            ? "declared"
            : "undeclared";
          // console.log(
            // "Showing toast (from call state):",
            // `Interest ${action} successfully!`,
          // );
          toast.showSuccess(`Interest ${action} successfully!`);
        } else {
          // Generic success message if we can't determine the state
          // console.log("Showing generic success toast");
          toast.showSuccess("Interest updated successfully!");
        }
      },
    );

    const unsubscribeError = updateCallInterestFx.failData.watch((error) => {
      console.error(
        "Component: updateCallInterestFx.failData triggered:",
        error,
      );
      toast.showError("Failed to update interest");
    });

    return () => {
      // console.log("Cleaning up interest effect watchers");
      unsubscribeSuccess();
      unsubscribeError();
    };
  }, [toast, call]);

  // Handle successful comment creation
  useEffect(() => {
    const unsubscribe = createCommentFx.doneData.watch(() => {
      toast.showSuccess("Comment posted successfully!");
      setNewComment("");
      setReplyText("");
      setReplyingTo(null);

      // Refetch call data to update comments count
      if (id) {
        fetchCallById(id);
      }
    });

    return unsubscribe;
  }, [toast, id, fetchCallById]);

  // Handle successful interest action
  useEffect(() => {
    const unsubscribe = updateCallInterestFx.doneData.watch((result) => {
      // Refetch call data to update interest status and partners count
      if (id) {
        fetchCallById(id);
      }

      // Show toast notification for interest changes
      if (result && result.is_call_declared_interest) {
        toast.showSuccess("Interest declared successfully!");
      } else if (result && !result.is_call_declared_interest) {
        toast.showSuccess("Interest undeclared successfully!");
      }
    });

    return unsubscribe;
  }, [id, fetchCallById, toast]);

  // Handle successful bookmark action
  useEffect(() => {
    const unsubscribe = updateCallBookmarkFx.doneData.watch(() => {
      // Refetch call data to update bookmark status
      if (id) {
        fetchCallById(id);
      }
    });

    return unsubscribe;
  }, [id, fetchCallById]);

  // Handle successful idea creation
  useEffect(() => {
    const unsubscribe = createIdeaFx.doneData.watch(() => {
      // Refetch call data to update ideas count
      if (id) {
        fetchCallById(id);
      }
    });

    return unsubscribe;
  }, [id, fetchCallById]);

  const handleBookmarkAction = async (
    callId: string,
    isBookmarked: boolean,
  ) => {
    // The effect handles the API call, just trigger the event
    updateBookmark({ callId, isBookmarked: !isBookmarked });
  };
  const handleInterestAction = async (
    callId: string,
    isBookmarked: boolean,
  ) => {
    // The effect handles the API call, just trigger the event
    // console.log("handleInterestAction called:", {
      // callId,
      // isBookmarked,
      // newValue: !isBookmarked,
    // });
    updateInterest({ callId, isBookmarked: !isBookmarked });
  };

  if (loading) {
    return (
      <div className="call-detail-page">
        <div className="pageTitle">Call Details</div>
        <Loader overlay />
      </div>
    );
  }
  if (error) {
    return (
      <div className="call-detail-page">
        <div className="pageTitle">Call Details</div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="call-detail-page">
        <div className="pageTitle">Call Details</div>
        <div className="error-message">Call not found</div>
      </div>
    );
  }

  const daysLeft = call?.deadline
    ? Math.ceil(
        (new Date(call.deadline).getTime() - new Date().getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : 0;

  const formatCommentDate = (timestamp: string) => {
    // Convert Unix timestamp to Date
    const date = new Date(parseInt(timestamp) * 1000);
    return (
      date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) +
      " " +
      date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    );
  };

  const getInitials = (name: string) => {
    if (!name || typeof name !== "string") return "A";

    return (
      name
        .trim()
        .split(" ")
        .filter((word) => word.length > 0)
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "A"
    );
  };

  const handleSubmitComment = () => {
    if (!call?.id || !newComment.trim()) return;

    createComment({
      call_id: call.id,
      body: newComment.trim(),
    });
  };

  const handleSubmitReply = (commentId: string) => {
    if (!call?.id || !replyText.trim()) return;

    createComment({
      call_id: call.id,
      body: replyText.trim(),
      reply_to_cid: commentId,
    });
  };

  const handleReplyClick = (commentId: string) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    setReplyText("");
  };

  // Helper function to map sector IDs to sector names
  const getSectorNameById = (sectorId: string): string => {
    if (!sectorsTerms.data || sectorsTerms.data.length === 0) {
      return sectorId; // fallback to ID if terms not loaded
    }
    const sector = sectorsTerms.data.find((term) => term.tid === sectorId);
    return sector ? sector.name : sectorId; // fallback to ID if name not found
  };

  // Helper function to render blurred partner placeholder
  const renderBlurredPartnerCard = (index: number) => {
    const placeholderNames = [
      "TechCorp Solutions",
      "EcoGreen Initiative",
      "Innovation Labs",
      "Digital Partners",
      "Research Hub",
    ];
    const placeholderTypes = [
      "Private Company",
      "Non-profit",
      "Research Institute",
      "SME",
      "University",
    ];
    const placeholderCountries = [
      "Germany",
      "Netherlands",
      "France",
      "Spain",
      "Italy",
    ];
    const placeholderSectors = [
      ["Technology", "Innovation", "AI"],
      ["Environment", "Sustainability", "Climate"],
      ["Research", "Development", "Science"],
    ];

    return (
      <div
        key={`blurred-partner-${index}`}
        className="partner-item eu-card"
        style={{
          filter: `blur(${6 - index * 1.5}px)`,
          opacity: 1 - index * 0.1,
        }}
      >
        <div className="partner-header">
          <div className="partner-logo">
            <div
              className="partner-logo-placeholder"
              style={{ display: "flex" }}
            >
              <span>{String.fromCharCode(65 + index)}</span>
            </div>
          </div>
          <div className="partner-info">
            <h3 className="partner-title">
              <span>{placeholderNames[index % placeholderNames.length]}</span>
            </h3>
            <div className="partner-meta">
              <div className="meta-item">
                <SvgIcon icon="icon-building" />
                <span>{placeholderTypes[index % placeholderTypes.length]}</span>
              </div>
              <div className="meta-item">
                <SvgIcon icon="icon-location" />
                <span>
                  {placeholderCountries[index % placeholderCountries.length]}
                </span>
              </div>
              <div className="meta-item">
                <SvgIcon icon="icon-clock" />
                <span>{2015 + index}</span>
              </div>
              <div className="meta-item">
                <SvgIcon icon="icon-users" />
                <span>{index % 2 === 0 ? "50-200" : "10-50"}</span>
              </div>
              <div className="meta-item">
                <SvgIcon icon="icon-projects" />
                <span>{8 + index * 2} projects</span>
              </div>
            </div>
          </div>
        </div>
        <div className="partner-sectors">
          {placeholderSectors[index % placeholderSectors.length].map(
            (sector, sIdx) => (
              <span key={sIdx} className="sector-tag">
                {sector}
              </span>
            ),
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="call-detail-page">
        {call && call.title && (
          <SEO title={`${call.title} | EUcalls`} description={call.title} />
        )}
        <div className="call-header">
          <Button
            icon="pi pi-arrow-left"
            text
            className="back-button"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </div>

        <div className="call-content">
          <div
            className={`call-main ${showExpandButton && !isExpanded ? "height-matched" : ""} ${
              isExpanded ? "expanded" : ""
            }`}
            ref={mainRef}
          >
            <div className="call-header-top">
              <div className="call-type-badge">
                <img src={call?.programme?.logo} />
                <span>{call?.programme?.name}</span>
              </div>
              <div className="call-actions-row">
                <Button
                  className="exportButton"
                  onClick={() => {
                    if (pro_user) {
                      handleExportCall();
                    } else {
                      setShowPayModal(true);
                    }
                  }}
                  disabled={isExporting}
                >
                  <SvgIcon icon="icon-export" />
                  {isExporting ? "Exporting call" : "Export call"}{" "}
                  <Tags type="pro" />
                  <SvgIcon icon="icon-help" />
                </Button>

                <Share />
                <Button
                  icon={
                    call?.is_call_bookmarked
                      ? "pi pi-bookmark-fill"
                      : "pi pi-bookmark"
                  }
                  text
                  label=""
                  className="action-btn"
                  onClick={() =>
                    handleBookmarkAction(call?.id, call?.is_call_bookmarked)
                  }
                />
              </div>
            </div>

            <h1 className="call-title" ref={titleRef}>
              {call?.title}
            </h1>

            <div className="call-metadata" ref={metadataRef}>
              <span className="meta-item funding">
                <SvgIcon icon="icon-funding" />
                Funding:{" "}
                <strong>
                  €{" "}
                  {call?.budget
                    ? parseInt(call.budget).toLocaleString()
                    : "N/A"}
                </strong>
              </span>

              <span className="meta-item">
                <SvgIcon icon="icon-calldate" />
                until{" "}
                <strong>
                  {call?.deadline
                    ? new Date(call.deadline).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : "N/A"}
                </strong>
              </span>
              <span className="days-left">
                {daysLeft > 0 ? `${daysLeft} days left` : "Expired"}
              </span>
            </div>
            <span className="expenditure">
              <SvgIcon icon="icon-expenditure" />
              Expenditure:
              <strong>
                {call?.expenditures &&
                  call.expenditures.length > 0 &&
                  call.expenditures.map((e) => e.name).join(", ")}
              </strong>
            </span>
            <div className="call-tags" ref={tagsRef}>
              {call?.sectors &&
                (showAllSectors
                  ? call.sectors.map((sector) => (
                      <span key={sector.id} className="tag-item">
                        {sector.name}
                      </span>
                    ))
                  : call.sectors.slice(0, 4).map((sector) => (
                      <span key={sector.id} className="tag-item">
                        {sector.name}
                      </span>
                    )))}
              {call?.sectors && call.sectors.length > 4 && (
                <button
                  onClick={() => setShowAllSectors(!showAllSectors)}
                  className="tag-item more-count"
                >
                  {showAllSectors ? "Show less" : `+${call.sectors.length - 4}`}
                </button>
              )}
            </div>

            <div className="call-description" ref={contentRef}>
              <h3>Description</h3>
              <div className="description-content">
                {call?.description && (
                  <div dangerouslySetInnerHTML={{ __html: call.description }} />
                )}

                {/* <Button label="Show more" icon="pi pi-angle-down" text className="show-more-description-btn" /> */}
              </div>
            </div>

            {showExpandButton && (
              <div className="expand-button-container">
                <Button
                  iconPos="right"
                  label={isExpanded ? "Show less" : "Show more"}
                  icon={isExpanded ? "pi pi-angle-up" : "pi pi-angle-down"}
                  text
                  className="show-more-description-btn"
                  onClick={() => setIsExpanded(!isExpanded)}
                />
              </div>
            )}
          </div>

          <div className="call-sidebar" ref={sidebarRef}>
            {!pro_user && (
              <div className="nonProStats">
                <div className="nonProStats-item">
                  <div className="nonProStats-title">Interested Partners</div>
                  <div className="nonProStats-partnersTag">
                    <Tags
                      number={
                        call?.interested_partners_count
                          ? call?.interested_partners_count
                          : 0
                      }
                      type="partner"
                    />
                  </div>
                </div>
                <div className="nonProStats-item">
                  <div className="nonProStats-title">Comments</div>
                  <Tags number={call?.comments_count} type="comment" />
                </div>
              </div>
            )}
            <div className="match-cards">
              {pro_user &&
                callGoals &&
                callGoals
                  .filter((e) => e.score)
                  .map((goal) => {
                    const scoreValue =
                      goal.score !== false && typeof goal.score === "string"
                        ? parseInt(goal.score)
                        : 0;
                    const getScoreColor = (score: number) => {
                      if (score >= 67) return "#067647"; // green
                      if (66 >= score && score >= 34) return "#f97316"; // orange
                      return "#ef4444"; // red
                    };

                    return (
                      <div key={goal.id} className="match-card equipment-goal">
                        <div className="match-header">
                          {goal.score !== false ? (
                            <>
                              <div className="match-percentage">
                                <div className="match-header-text">
                                  {scoreValue}% match
                                </div>
                                <div
                                  className="percentage-fill"
                                  style={{
                                    width: `${scoreValue}%`,
                                    backgroundColor: getScoreColor(scoreValue),
                                  }}
                                />
                              </div>
                              <div className="percentage-bar"></div>
                            </>
                          ) : (
                            <div className="match-percentage not-analyzed">
                              Not analyzed yet
                            </div>
                          )}
                        </div>
                        <div className="match-body">
                          <div className="match-title">{goal.title}</div>
                          <Button
                            icon="pi pi-arrow-right"
                            text
                            className="match-arrow"
                            onClick={() => {
                              navigate(`/goals/${goal.id}/matches`);
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
            </div>
            {pro_user && (
              <div className="stats-section">
                <div
                  className="interested-partners"
                  style={{
                    flexDirection: "column",
                    alignItems: "start",
                    gap: "1rem",
                  }}
                >
                  <span className="partners-label">Interested Partners</span>
                  {!call?.interested_partners ||
                    (call?.interested_partners.length === 0 && (
                      <div className="partners-avatars">
                        <Tags
                          number={
                            call?.interested_partners_count
                              ? call?.interested_partners_count
                              : 0
                          }
                          type="partner"
                        />
                      </div>
                    ))}
                  {call?.interested_partners &&
                    call?.interested_partners?.length > 0 && (
                      <div className="partners-avatars">
                        {call?.interested_partners?.slice(0, 4).map((p, i) => (
                          <div
                            key={p.id || i}
                            className="partner-avatar-wrapper"
                            style={{
                              transform:
                                i > 0 ? `translateX(-${30 * i}px)` : "none",
                              zIndex: i > 0 ? 2 + (i - 1) : 1,
                            }}
                          >
                            <div className="company-logo placeholder">
                              <BgImage
                                img={p.logo_uri}
                                altImage={imageList.noOrgimage}
                              />
                            </div>
                          </div>
                        ))}

                        {call.interested_partners.length > 4 && (
                          <span
                            className="more-count"
                            style={{
                              transform: `translateX(-${30 * call.interested_partners.slice(0, 4).length}px)`,
                              zIndex: 2 + call.interested_partners.length,
                            }}
                          >
                            +{call.interested_partners.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                </div>
                <div className="comments-statSection">
                  <span className="comments-label">Comments</span>
                  <Tags type="comment" number={call?.comments_count || 0} />
                </div>
              </div>
            )}

            <div className="action-buttons">
              <div
                className={`declare-interest-btn ${call?.is_call_declared_interest ? "declared" : ""}`}
                onClick={() => {
                  setShowProModal(true);
                }}
              >
                <SvgIcon icon="icon-declare" />
                {call?.is_call_declared_interest
                  ? "Undeclare interest"
                  : "Declare interest"}
              </div>
              {call?.official_link &&
              call?.official_link !== "" &&
              call?.official_link !== "pro-feature" ? (
                <a
                  href={call?.official_link}
                  className="official-link-btn"
                  target="_blank"
                  onClick={() => {
                    // if (call?.official_link !== 'pro-feature') navigate(call?.official_link);
                  }}
                >
                  <i className="pi pi-external-link"></i>
                  <span>Visit official link</span>
                  {call?.official_link === "pro-feature" && (
                    <span className="pro-tag">Pro</span>
                  )}
                </a>
              ) : (
                <>
                  <div
                    className="official-link-btn"
                    onClick={() => {
                      setShowPayModal(true);
                    }}
                  >
                    <i className="pi pi-external-link"></i>
                    <span>Visit official link</span>
                    {call?.official_link === "pro-feature" && (
                      <span className="pro-tag">Pro</span>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="assigned-ideas-section">
              <div className="ideas-header">
                <SvgIcon icon="icon-idea" />
                <span>
                  {call.related_ideas.length} assigned{" "}
                  {call.related_ideas.length > 1 ? "Ideas" : "Idea"}
                </span>
              </div>

              {ideasToShow && ideasToShow.length > 0 ? (
                <div className="ideas-list">
                  {ideasToShow.map((idea) => (
                    <div key={idea.id} className="idea-card">
                      <div className="idea-badges">
                        {/* {idea.status === 'Published' && <span className="badge published">Published</span>} */}
                        {idea.is_coordinator && (
                          <span className="badge guest">Coordinator</span>
                        )}
                        {idea.is_member && !idea.is_owner && (
                          <span className="badge guest">Guest</span>
                        )}
                        {idea.is_owner && (
                          <span className="badge guest">Owner</span>
                        )}
                      </div>

                      <Link to={`/ideas/${idea.id}`} className="idea-title">
                        {idea.title}
                      </Link>
                      <p className="idea-description">{idea.description}</p>

                      <div className="idea-tags">
                        {idea.sectors?.slice(0, 3).map((sector) => (
                          <Tag
                            key={sector.id}
                            value={sector.name}
                            className="sector-tag"
                          />
                        ))}
                        {idea.sectors && idea.sectors.length > 3 && (
                          <span className="more-tags">
                            +{idea.sectors.length - 3}
                          </span>
                        )}
                      </div>

                      <div className="idea-meta">
                        <div className="partnership-section">
                          <span className="label">Partnership</span>
                          <div className="partnership-logos">
                            {idea.is_member || idea.is_owner ? (
                              idea.partnership &&
                              (Array.isArray(idea.partnership)
                                ? idea.partnership.length > 0
                                : Object.keys(idea.partnership).length > 0) && (
                                <div className="interested-partners">
                                  <div className="partners-avatars">
                                    {Object.values(idea?.partnership)
                                      ?.slice(0, 4)
                                      .map((p, i) => (
                                        <div
                                          key={p.id || i}
                                          className="partner-avatar-wrapper"
                                          style={{
                                            transform:
                                              i > 0
                                                ? `translateX(-${30 * i}px)`
                                                : "none",
                                            zIndex: i > 0 ? 2 + (i - 1) : 1,
                                          }}
                                        >
                                          <div className="company-logo placeholder">
                                            <BgImage
                                              img={p.logo_uri}
                                              altImage={imageList.noOrgimage}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )
                            ) : (
                              <div>
                                {idea.partnership &&
                                  Array.isArray(idea.partnership) && (
                                    <Tags
                                      type="partner"
                                      number={idea.partnership_count}
                                    />
                                  )}
                              </div>
                            )}
                            {idea.partnership && Array.isArray(idea.partnership)
                              ? idea.partnership.length > 0
                              : Object.keys(idea.partnership).length > 0 &&
                                Object.keys(idea.partnership).length > 4 && (
                                  <span
                                    className="more-count"
                                    style={{
                                      transform: `translateX(-${28 * 4}px) translateY(-11px)`,
                                      zIndex:
                                        2 +
                                        Object.values(idea?.partnership).length,
                                    }}
                                  >
                                    +
                                    {Object.values(idea?.partnership).length -
                                      4}
                                  </span>
                                )}
                          </div>
                        </div>

                        <div className="looking-for-section">
                          <span className="label">Looking for</span>
                          {idea.looking_for && idea.looking_for.length > 0 ? (
                            <span className="looking-for-text">
                              {[
                                idea.looking_for?.map((item) => item.name) ||
                                  [],
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          ) : (
                            <span className="looking-for-text">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {call.related_ideas.length > 2 && (
                    <Button
                      label={ideasToShowExpanded ? "Show Less" : "Show more"}
                      icon={
                        ideasToShowExpanded
                          ? "pi pi-angle-up"
                          : "pi pi-angle-down"
                      }
                      iconPos="right"
                      text
                      className="show-more-ideas-btn"
                      onClick={() => {
                        handleSetIdeasToShow(!ideasToShowExpanded);
                        setIdeasToShowExpanded(!ideasToShowExpanded);
                      }}
                    />
                  )}
                </div>
              ) : (
                <div className="no-ideas-placeholder">
                  <p>No assigned ideas yet</p>
                  <p className="placeholder-hint">
                    Be the first one to create an idea and stand out to your
                    partners
                  </p>
                </div>
              )}
            </div>

            <div
              className={`add-idea-btn ${typeof userBasicInfo?.partner === "string" ? "disabled" : ""}`}
              onClick={() => {
                if (typeof userBasicInfo?.partner === "object") {
                  setShowIdeaModal(true);
                }
              }}
            >
              <SvgIcon icon="icon-idea" /> Add an idea
            </div>

            {typeof userBasicInfo?.partner === "string" && (
              <div className="ideaCreateOrg">
                <Link to={`/settings/organization`}>
                  Create an organisation
                </Link>{" "}
                to add an idea
              </div>
            )}
          </div>
        </div>
        {typeof userBasicInfo?.partner === "object" ? (
          <div className="partners-section">
            <div className="partners-header">
              <h3>
                Interested Partners (
                {call?.interested_partners_count || partnersToShow.length})
              </h3>

              {typeof userBasicInfo?.partner === "object" &&
                call?.interested_partners &&
                call?.interested_partners.length > 0 && (
                  <div className="navigation-arrows">
                    <Button
                      icon="pi pi-chevron-left"
                      text
                      className="nav-arrow"
                      onClick={handlePreviousPartner}
                      disabled={totalSlides <= 1}
                    />
                    <Button
                      icon="pi pi-chevron-right"
                      text
                      className="nav-arrow"
                      onClick={handleNextPartner}
                      disabled={totalSlides <= 1}
                    />
                  </div>
                )}
            </div>

            <div className="partners-carousel">
              {partnersToShow.length > 0 ? (
                <div
                  className="partners-track"
                  ref={partnersRef}
                  style={{
                    transform: `translateX(-${currentPartnerIndex * 100}%)`,
                  }}
                >
                  {partnersToShow.map((partner) => (
                    <div key={partner.id} className="partner-item eu-card">
                      <div className="partner-header">
                        <div className="partner-logo">
                          {partner.logo_uri ? (
                            <img
                              src={partner.logo_uri}
                              alt={partner.title}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = "none";
                                const placeholder =
                                  target.nextElementSibling as HTMLElement;
                                if (placeholder)
                                  placeholder.style.display = "flex";
                              }}
                            />
                          ) : (
                            <img
                              src={imageList.noOrgimage}
                              alt={imageList.noOrgimage}
                            />
                          )}
                        </div>
                        <div className="partner-info">
                          <h3 className="partner-title">
                            <Link to={`/partner/${partner.id}`}>
                              {partner.title}
                            </Link>
                          </h3>
                          <div className="partner-meta">
                            {partner.partner_type && (
                              <div className="meta-item">
                                <SvgIcon icon="icon-building" />
                                <span>{partner.partner_type.name}</span>
                              </div>
                            )}
                            {partner.country && (
                              <div className="meta-item">
                                <SvgIcon icon="icon-location" />
                                <span>{partner.country.name}</span>
                              </div>
                            )}
                            <div className="meta-item">
                              <SvgIcon icon="icon-clock" />
                              <span>
                                {partner.since ||
                                  (partner.field_created
                                    ? new Date(
                                        parseInt(partner.field_created) * 1000,
                                      )
                                        .getFullYear()
                                        .toString()
                                    : "N/A")}
                              </span>
                            </div>
                            {partner.size && (
                              <div className="meta-item">
                                <SvgIcon icon="icon-users" />
                                <span>{partner.size.name}</span>
                              </div>
                            )}
                            <div className="meta-item">
                              <SvgIcon icon="icon-projects" />
                              <span>
                                {partner.implemented_projects || 0} projects
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="partner-sectors">
                        {partner.sectors &&
                          Array.isArray(partner.sectors) &&
                          partner.sectors.slice(0, 3).map((sector) => (
                            <span key={sector.id} className="sector-tag">
                              {sector.name}
                            </span>
                          ))}
                        {partner.sectors && partner.sectors.length > 3 && (
                          <span className="sector-tag more-count">
                            +{partner.sectors.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : call?.interested_partners_count &&
                call.interested_partners_count > 0 ? (
                // Show blurred placeholders when we have a count but no actual data
                <div className="partners-carousel-locked">
                  <div className="noPro-card">
                    <div className="noPro-card-title">
                      View partners that have declared interest in this call
                    </div>
                    <div className="noPro-card-text">
                      Get full access to all Pro features with a paid plan
                    </div>
                    <div className="noPro-card-action">
                      <Button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/settings/subscription")}
                      >
                        Upgrade now
                      </Button>
                    </div>
                  </div>
                  <div
                    className="partners-track partners-track-blurred"
                    style={{
                      padding: "1rem",
                      backgroundColor: "#fff",
                      borderRadius: ".4rem",
                    }}
                  >
                    {Array.from({
                      length: Math.min(call.interested_partners_count, 3),
                    }).map((_, index) => renderBlurredPartnerCard(index))}
                  </div>
                </div>
              ) : (
                <div className="no-partners-placeholder">
                  <p>No interested partners yet</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="partners-section partners-section-mock">
            <div className="partners-header">
              <h3>
                Interested Partners ({call?.interested_partners_count || 0})
              </h3>
            </div>

            <div className="partners-carousel">
              <div className="noPro-card">
                <div className="noPro-card-title">
                  View partners that have declared interest in this call
                </div>
                <div className="noPro-card-text">
                  Get full access to all Prο features with a paid plan
                </div>
                <div className="noPro-card-action">
                  <Button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/settings/subscription")}
                  >
                    Upgrade Now
                  </Button>
                </div>
              </div>
              <div
                className="partners-track"
                style={{
                  transform: `translateX(0%)`,
                  padding: "1rem",
                  backgroundColor: "#fff",
                  borderRadius: ".4rem",
                  opacity: "0.7",
                }}
              >
                {Array.from({
                  length: Math.min(call?.interested_partners_count || 2, 3),
                }).map((_, index) => renderBlurredPartnerCard(index))}
              </div>
            </div>
          </div>
        )}
        <div className="comments-header">
          <h3>Comments ({call?.comments_count || comments.length})</h3>
        </div>
        <div className="comments-section">
          {commentsLoading ? (
            <div className="comments-loading">
              <Loader />
            </div>
          ) : commentsError ? (
            <div className="comments-error">
              <p>Error loading comments: {commentsError}</p>
            </div>
          ) : (
            <div className="comments-list">
              {comments.length > 0 ? (
                (() => {
                  // Separate main comments and replies
                  const mainComments = comments.filter(
                    (comment) =>
                      !comment.parent_id || comment.parent_id === "0",
                  );
                  const replies = comments.filter(
                    (comment) => comment.parent_id && comment.parent_id !== "0",
                  );

                  return mainComments.map((comment) => {
                    const commentReplies = replies.filter(
                      (reply) => reply.parent_id === comment.id,
                    );

                    return (
                      <div key={comment.id} className="comment-thread">
                        <div className="comment-item">
                          <div className="comment-avatar">
                            <div className="avatar placeholder">
                              <BgImage img={comment.user_profile_image} />
                            </div>
                          </div>
                          <div className="comment-content">
                            <Link to={`/users/${comment.user_id}`}>
                              <div className="comment-header">
                                <span className="comment-author">
                                  {comment.user_profile_name}
                                </span>
                                <span className="comment-time">
                                  {formatCommentDate(comment.created_time)}
                                </span>
                              </div>
                            </Link>

                            <div
                              className="comment-text"
                              dangerouslySetInnerHTML={{ __html: comment.body }}
                            />
                            <div
                              className="reply-btn"
                              onClick={() => handleReplyClick(comment.id)}
                            >
                              <SvgIcon icon="icon-reply" />
                              Reply
                            </div>
                          </div>
                        </div>

                        {/* Show replies */}
                        {commentReplies.map((reply) => (
                          <div
                            key={reply.id}
                            className="comment-item reply-comment"
                          >
                            <div className="comment-avatar">
                              <div className="avatar placeholder">
                                <BgImage img={imageList.noimage} />
                              </div>
                            </div>
                            <div className="comment-content">
                              <div className="comment-header">
                                <span className="comment-author">
                                  {reply.name}
                                </span>
                                <span className="comment-time">
                                  {formatCommentDate(reply.created_time)}
                                </span>
                              </div>
                              <div
                                className="comment-text"
                                dangerouslySetInnerHTML={{ __html: reply.body }}
                              />
                            </div>
                          </div>
                        ))}

                        {/* Reply form */}
                        {replyingTo === comment.id && (
                          <div className="reply-form">
                            <div className="comment-input">
                              <textarea
                                placeholder="Write your reply here..."
                                rows={2}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                              />
                            </div>
                            <div className="reply-actions">
                              <Button
                                label="Cancel"
                                text
                                className="btn-secondary"
                                onClick={() => setReplyingTo(null)}
                              />
                              <Button
                                label={
                                  createCommentLoading
                                    ? "Posting..."
                                    : "Post Reply"
                                }
                                className="btn-primary"
                                disabled={
                                  createCommentLoading || !replyText.trim()
                                }
                                onClick={() => handleSubmitReply(comment.id)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()
              ) : (
                <div className="no-comments">
                  <div className="iconContainer">
                    <SvgIcon icon="icon-comments" />
                  </div>
                  <p>
                    <strong>No comments yet</strong>
                  </p>
                  <p>Write a comment and start the conversation</p>
                </div>
              )}
            </div>
          )}

          <div className="comment-form">
            <div className="comment-avatar">
              <div className="avatar placeholder">
                <BgImage img={imageList.noimage} />
              </div>
            </div>
            <div className="comment-input">
              <textarea
                placeholder="Write your comment here..."
                rows={3}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <div className="comment-actions">
                <Button
                  label={createCommentLoading ? "Posting..." : "Post Comment"}
                  className="btn-primary"
                  disabled={createCommentLoading || !newComment.trim()}
                  onClick={handleSubmitComment}
                />
              </div>
              {createCommentError && (
                <div className="comment-error">
                  <p>Error posting comment: {createCommentError}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <CommonModal
        isOpen={showIdeaModal}
        closeIcon
        onClose={() => {
          setModalIsOpened(false);
          setShowIdeaModal(false);
        }}
        width={590}
        height={500}
        icon="icon-idea"
        hasForm={true}
        formId="idea-form"
        onSubmit={handleSubmit(onSubmitIdea)}
        actions={
          <div className="form-actions">
            <button
              type="submit"
              form="idea-form"
              className="btn-secondary"
              onClick={() => {
                setIsDraft(true);
              }}
            >
              Save as draft
            </button>
            <button
              type="submit"
              form="idea-form"
              className="btn-primary"
              disabled={createIdeaLoading}
              onClick={() => setIsDraft(false)}
            >
              {createIdeaLoading ? "Submitting..." : "Submit for review"}
            </button>
          </div>
        }
      >
        <div
          className="ideaModal"
          style={{
            position: "relative",
            overflowY: "scroll",
            height: "100%",
          }}
        >
          <div className="ideaModal-title">Add your idea</div>
          <div className="ideaModal-description">
            By adding your idea you make it easier for collaborators to reach
            out to you to create a potential partnership. You also declare
            interest for this call
          </div>
          <div className="ideaModal-form">
            <div className="form-field">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                placeholder="What's your idea title?"
                {...register("title", { required: "Title is required" })}
              />
              {errors.title && (
                <span className="error">{errors.title.message}</span>
              )}
            </div>

            <div className="form-field">
              <label>Looking for</label>
              <div className="checkbox-group">
                {lookingForTerms.data.map((term) => (
                  <label key={term.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      value={term.tid}
                      {...register("lookingFor", {
                        required: "Looking for Field is required",
                      })}
                    />
                    {term.name}
                  </label>
                ))}
              </div>
              {errors.lookingFor && (
                <span className="error">{errors.lookingFor.message}</span>
              )}
            </div>

            <div className="form-field">
              <label>With EU expertise in</label>
              <div className="checkbox-group">
                {expertiseTerms.data.map((term) => (
                  <label key={term.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      value={term.tid}
                      {...register("expertise", {
                        required: "Expertise Field is required",
                      })}
                    />
                    {term.name}
                  </label>
                ))}
              </div>
              {errors.expertise && (
                <span className="error">{errors.expertise.message}</span>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                rows={4}
                placeholder="e.g.This project proposes a digital solution to enhance citizen engagement in local policymaking. The concept focuses on developing a multilingual platform co-created with municipalities and civic tech partners."
                {...register("description", {
                  required: "Description is required",
                })}
              ></textarea>
              {errors.description && (
                <span className="error">{errors.description.message}</span>
              )}
            </div>
          </div>
        </div>
      </CommonModal>
      <CommonModal
        onClose={() => {
          setShowProModal(false);
          setModalIsOpened(false);
        }}
        hasForm={false}
        width={530}
        height={
          !call?.is_call_declared_interest &&
          typeof userBasicInfo?.partner === "object"
            ? 600
            : 470
        }
        icon={"icon-checkboxes"}
        isOpen={showProModal}
        actions={
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowProModal(false);
                setModalIsOpened(false);
              }}
            >
              Cancel
            </button>
            <Button
              type="button"
              form="edit-idea-form"
              severity={`${(call?.id, call?.is_call_declared_interest) ? "danger" : "secondary"}`}
              className={`${(call?.id, call?.is_call_declared_interest) ? "btn-danger" : "btn-primary"}`}
              onClick={() => {
                handleInterestAction(call?.id, call?.is_call_declared_interest);
                setShowProModal(false);
                setModalIsOpened(false);
              }}
            >
              {
                (call?.id,
                call?.is_call_declared_interest
                  ? "Undeclare interest"
                  : "Declare interest")
              }
            </Button>
          </div>
        }
      >
        <div className="proModal">
          {call?.is_call_declared_interest ? (
            <div className="proModal-title">Undeclare interest</div>
          ) : (
            <div className="proModal-title">
              Declare interest for "{call.title}"
            </div>
          )}
          <div className="proModal-description flex flex-col gap-1">
            {call?.is_call_declared_interest ? (
              <div>
                <p>Are you sure you want to withdraw your interest? </p>
                <p>
                  Other partners will no longer see you in the Interested
                  Partners section or contact you for collaboration.
                </p>
              </div>
            ) : (
              <div>
                <p>
                  By declaring interest you let other collaborators know about
                  it, and make it easier for them to reach out to you to create
                  a potential partnership.
                </p>
              </div>
            )}

            {!call?.is_call_declared_interest &&
              typeof userBasicInfo?.partner === "object" && (
                <div className="proModal-addIdea addAnIdeaModal">
                  <label className="proModal-addIdea-title">
                    Share your funding idea for this call
                  </label>
                  <div className="proModal-addIdea-text">
                    Present your project idea to attrack like-minded partners
                    and kickstart meaningfull collaboration
                  </div>
                  <div
                    className="modal-addIdea"
                    onClick={() => {
                      setShowProModal(false);
                      setModalIsOpened(false);
                      setShowIdeaModal(true);
                    }}
                  >
                    <SvgIcon icon="icon-idea" />
                    Add your idea
                  </div>
                </div>
              )}
          </div>
        </div>
      </CommonModal>
      <CommonModal
        onClose={() => {
          setShowPayModal(false);
          setModalIsOpened(false);
        }}
        hasForm={false}
        width={430}
        height={470}
        icon={"icon-lighting"}
        isOpen={showPayModal}
        actions={
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowPayModal(false);
                setModalIsOpened(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              form="edit-idea-form"
              className="btn-primary"
              onClick={() => navigate("/settings/subscription")}
            >
              View plans
            </button>
          </div>
        }
      >
        <div className="proModal">
          <div className="proModal-title">
            Get full access to Pro features with a paid plan
          </div>
          <div className="proModal-description flex flex-col gap-1">
            You could do so much more with our Professional and Premium plans.
            Unlock features like:
            <div className="proModal-list">
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" /> Personalized calls just for you
              </div>
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" />
                Calls' interested partners
              </div>
              <div className="flex gap-05">
                <SvgIcon icon="icon-check" />
                Funding goals
              </div>
            </div>
          </div>
        </div>
      </CommonModal>
    </>
  );
};
